import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const ManufacturingLogistics = () => {
  return (
    <>
      <SEO 
        title="Manufacturing & Logistics Solutions - Smart Factory & Supply Chain AI"
        description="Transform manufacturing operations with AI-powered automation, predictive maintenance, supply chain optimization, and Industry 4.0 solutions. Reduce downtime by 40% and increase efficiency."
        keywords="manufacturing AI, smart factory, Industry 4.0, supply chain optimization, predictive maintenance, logistics automation, IoT manufacturing, quality control AI"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-charcoal via-graphite to-charcoal text-white py-10 md:py-10 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(230, 57, 70, 0.1) 2px, rgba(230, 57, 70, 0.1) 4px),
                             repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(230, 57, 70, 0.1) 2px, rgba(230, 57, 70, 0.1) 4px)`
          }}></div>
        </div>
        
        <div className="page-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-primary-red/20 backdrop-blur-sm border border-primary-red/30 rounded-full mb-6 animate-fade-in">
              <span className="text-coral-mist font-semibold">Industry 4.0 Solutions</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-slide-up">
              Smart Manufacturing & <span className="text-gradient bg-gradient-to-r from-coral-mist to-primary-red bg-clip-text text-transparent">Intelligent Logistics</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Transform your manufacturing operations with AI-powered automation, predictive maintenance, and real-time supply chain optimization. Reduce downtime by 40%, increase productivity by 35%, and achieve operational excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="bg-primary-red text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-deep-garnet transition-all duration-300 shadow-lg hover:shadow-xl">
                Request Demo
              </Link>
              <a href="#solutions" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-charcoal transition-all duration-300">
                Explore Solutions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-5 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">40%</div>
              <div className="text-sm text-titanium">Downtime Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">35%</div>
              <div className="text-sm text-titanium">Productivity Increase</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">25%</div>
              <div className="text-sm text-titanium">Cost Savings</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">99.5%</div>
              <div className="text-sm text-titanium">Quality Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Challenges */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Manufacturing Challenges We Solve
            </h2>
            <p className="text-lg text-titanium">
              Modern manufacturing faces complex challenges that require intelligent solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Unplanned Downtime",
                description: "Equipment failures cause production delays and revenue loss",
                solution: "Predictive maintenance reduces breakdowns by 70%"
              },
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Supply Chain Disruptions",
                description: "Inventory shortages and logistics inefficiencies impact delivery",
                solution: "AI-powered forecasting ensures 99% on-time delivery"
              },
              {
                icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Quality Control Issues",
                description: "Manual inspections miss defects and slow production",
                solution: "Computer vision detects defects with 99.5% accuracy"
              },
              {
                icon: "M13 10V3L4 14h7v7l9-11h-7z",
                title: "Energy Inefficiency",
                description: "High energy costs and environmental concerns",
                solution: "Smart energy management reduces consumption by 30%"
              },
              {
                icon: "M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3",
                title: "Workforce Shortages",
                description: "Skilled labor gaps affect production capacity",
                solution: "Automation and AI augment workforce productivity"
              },
              {
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                title: "Data Silos",
                description: "Disconnected systems prevent real-time insights",
                solution: "Unified platform integrates all manufacturing data"
              }
            ].map((challenge, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-platinum">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={challenge.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-charcoal mb-2">{challenge.title}</h3>
                <p className="text-sm text-titanium mb-3">{challenge.description}</p>
                <div className="pt-3 border-t border-platinum">
                  <p className="text-sm font-medium text-primary-red">✓ {challenge.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Solutions */}
      <section id="solutions" className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Comprehensive Manufacturing Solutions
            </h2>
            <p className="text-lg text-titanium">
              End-to-end digital transformation for modern manufacturing operations
            </p>
          </div>

          <div className="space-y-12">
            {/* Predictive Maintenance */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-primary-red text-white text-sm rounded-full mb-4">
                  AI-Powered
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">
                  Predictive Maintenance & Asset Management
                </h3>
                <p className="text-titanium mb-6">
                  Leverage machine learning algorithms to predict equipment failures before they occur. Our AI analyzes sensor data, maintenance history, and operational patterns to schedule maintenance at optimal times, reducing unplanned downtime by up to 70%.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Real-time monitoring</strong> of equipment health and performance metrics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Failure prediction</strong> with 85% accuracy 3-7 days in advance</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Automated alerts</strong> and maintenance scheduling optimization</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">ROI tracking</strong> and maintenance cost reduction analytics</span>
                  </li>
                </ul>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact" className="btn-primary">
                    Start Pilot Program
                  </Link>
                  <Link to="/ai-hub/predictive-analytics" className="btn-secondary">
                    Learn More
                  </Link>
                </div>
              </div>
              <div className="bg-alabaster rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 text-center">
                    <div className="text-3xl font-bold text-primary-red mb-2">70%</div>
                    <div className="text-sm text-titanium">Downtime Reduction</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 text-center">
                    <div className="text-3xl font-bold text-primary-red mb-2">30%</div>
                    <div className="text-sm text-titanium">Maintenance Cost Cut</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 text-center">
                    <div className="text-3xl font-bold text-primary-red mb-2">85%</div>
                    <div className="text-sm text-titanium">Prediction Accuracy</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 text-center">
                    <div className="text-3xl font-bold text-primary-red mb-2">24/7</div>
                    <div className="text-sm text-titanium">Asset Monitoring</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quality Control */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1 bg-alabaster rounded-2xl p-8">
                <h4 className="text-lg font-semibold text-charcoal mb-4">Quality Metrics Impact</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-titanium">Defect Detection Rate</span>
                      <span className="font-semibold text-charcoal">99.5%</span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '99.5%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-titanium">Inspection Speed Increase</span>
                      <span className="font-semibold text-charcoal">500%</span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-titanium">False Positive Reduction</span>
                      <span className="font-semibold text-charcoal">90%</span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '90%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-titanium">Cost per Inspection</span>
                      <span className="font-semibold text-charcoal">-75%</span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block px-3 py-1 bg-primary-red text-white text-sm rounded-full mb-4">
                  Computer Vision
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">
                  AI-Powered Quality Control & Inspection
                </h3>
                <p className="text-titanium mb-6">
                  Replace manual inspection processes with computer vision systems that detect defects with superhuman accuracy. Our AI-powered quality control solutions inspect products at production speed, ensuring consistent quality standards across all manufacturing lines.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Automated visual inspection</strong> at 500x human speed</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Multi-defect detection</strong> including cracks, scratches, color variations</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Real-time reporting</strong> and quality trend analysis</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Integration</strong> with existing quality management systems</span>
                  </li>
                </ul>
                <Link to="/ai-hub/computer-vision" className="text-primary-red font-semibold hover:underline inline-flex items-center">
                  Explore Computer Vision Solutions
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Supply Chain Optimization */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-primary-red text-white text-sm rounded-full mb-4">
                  Smart Logistics
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">
                  Supply Chain Optimization & Logistics Intelligence
                </h3>
                <p className="text-titanium mb-6">
                  Transform your supply chain with AI-driven demand forecasting, inventory optimization, and route planning. Our intelligent logistics platform reduces inventory costs by 20-30% while ensuring 99% on-time delivery rates through predictive analytics and real-time optimization.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Demand forecasting</strong> with 95% accuracy using ML algorithms</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Inventory optimization</strong> reducing carrying costs by 25%</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Route optimization</strong> cutting transportation costs by 15%</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium"><strong className="text-charcoal">Supplier performance</strong> tracking and risk mitigation</span>
                  </li>
                </ul>
                <Link to="/services/process-automation" className="text-primary-red font-semibold hover:underline inline-flex items-center">
                  View Automation Solutions
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
              <div className="bg-alabaster rounded-2xl p-8">
                <h4 className="text-lg font-semibold text-charcoal mb-6">Supply Chain Benefits</h4>
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-titanium">Inventory Reduction</span>
                      <span className="text-lg font-bold text-primary-red">-25%</span>
                    </div>
                    <p className="text-xs text-titanium">Lower carrying costs through demand-driven stocking</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-titanium">On-Time Delivery</span>
                      <span className="text-lg font-bold text-primary-red">99%</span>
                    </div>
                    <p className="text-xs text-titanium">Improved customer satisfaction and repeat business</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-titanium">Order Fulfillment Speed</span>
                      <span className="text-lg font-bold text-primary-red">+40%</span>
                    </div>
                    <p className="text-xs text-titanium">Faster processing through intelligent automation</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-titanium">Transportation Costs</span>
                      <span className="text-lg font-bold text-primary-red">-15%</span>
                    </div>
                    <p className="text-xs text-titanium">Optimized routing and load planning</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Capabilities */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Complete Industry 4.0 Platform
            </h2>
            <p className="text-lg text-titanium">
              Integrated solutions for every aspect of modern manufacturing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "IoT Integration",
                description: "Connect and monitor all manufacturing equipment in real-time with our IoT platform",
                features: ["Sensor integration", "Real-time dashboards", "Edge computing"]
              },
              {
                title: "Production Planning",
                description: "AI-powered scheduling and resource allocation for maximum efficiency",
                features: ["Automated scheduling", "Resource optimization", "Bottleneck detection"]
              },
              {
                title: "Energy Management",
                description: "Reduce energy consumption and costs through intelligent monitoring and control",
                features: ["Smart metering", "Peak demand management", "Carbon tracking"]
              },
              {
                title: "Workforce Management",
                description: "Optimize labor allocation and enhance worker safety with smart systems",
                features: ["Skill matching", "Safety monitoring", "Productivity tracking"]
              },
              {
                title: "Digital Twin",
                description: "Create virtual replicas of physical assets for simulation and optimization",
                features: ["3D modeling", "Scenario testing", "Performance simulation"]
              },
              {
                title: "Compliance & Traceability",
                description: "Ensure regulatory compliance and full product traceability",
                features: ["Audit trails", "Quality documentation", "Regulatory reporting"]
              }
            ].map((capability, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300">
                <h3 className="text-xl font-semibold text-charcoal mb-3">{capability.title}</h3>
                <p className="text-titanium mb-4">{capability.description}</p>
                <ul className="space-y-2">
                  {capability.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-titanium">
                      <svg className="w-4 h-4 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-charcoal to-graphite text-white rounded-2xl p-8 md:p-12">
              <div className="inline-block px-3 py-1 bg-primary-red text-white text-sm rounded-full mb-6">
                Success Story
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Global Automotive Manufacturer Reduces Downtime by 65%
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                A Fortune 500 automotive manufacturer implemented our predictive maintenance and quality control solutions across 12 production facilities, achieving remarkable results in just 6 months.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-4xl font-bold text-coral-mist mb-2">65%</div>
                  <div className="text-gray-300">Reduction in unplanned downtime</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-4xl font-bold text-coral-mist mb-2">$8.5M</div>
                  <div className="text-gray-300">Annual cost savings achieved</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-4xl font-bold text-coral-mist mb-2">99.2%</div>
                  <div className="text-gray-300">Quality rate improvement</div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-6">
                <p className="text-gray-300 italic mb-4">
                  "The AI-powered predictive maintenance system has transformed our operations. We're now able to schedule maintenance during planned downtime, and our production efficiency has never been higher."
                </p>
                <p className="font-semibold">— Chief Operations Officer, Global Automotive OEM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Enterprise-Grade Technology
            </h2>
            <p className="text-lg text-titanium">
              Built on proven platforms and frameworks trusted by industry leaders
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { name: "Azure IoT", category: "Cloud Platform" },
              { name: "TensorFlow", category: "Machine Learning" },
              { name: "SAP S/4HANA", category: "ERP Integration" },
              { name: "Siemens MindSphere", category: "IIoT Platform" },
              { name: "AVEVA PI System", category: "Data Infrastructure" },
              { name: "Rockwell FactoryTalk", category: "Manufacturing Ops" },
              { name: "GE Predix", category: "Industrial Analytics" },
              { name: "PTC ThingWorx", category: "IoT Platform" }
            ].map((tech, index) => (
              <div key={index} className="bg-white rounded-lg p-4 text-center shadow-sm">
                <div className="font-semibold text-charcoal mb-1">{tech.name}</div>
                <div className="text-xs text-titanium">{tech.category}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Proven Implementation Methodology
            </h2>
            <p className="text-lg text-titanium">
              Structured approach ensuring successful deployment with minimal disruption
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  phase: "Phase 1",
                  title: "Assessment & Planning",
                  duration: "2-4 weeks",
                  activities: ["Current state analysis", "Pain point identification", "ROI modeling", "Solution design"]
                },
                {
                  phase: "Phase 2",
                  title: "Pilot Implementation",
                  duration: "6-8 weeks",
                  activities: ["Single line deployment", "System integration", "User training", "Performance validation"]
                },
                {
                  phase: "Phase 3",
                  title: "Full Deployment",
                  duration: "3-6 months",
                  activities: ["Multi-facility rollout", "Process optimization", "Change management", "Documentation"]
                },
                {
                  phase: "Phase 4",
                  title: "Optimization & Support",
                  duration: "Ongoing",
                  activities: ["Performance monitoring", "Continuous improvement", "Feature enhancements", "24/7 support"]
                }
              ].map((step, index) => (
                <div key={index} className="bg-alabaster rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div className="flex items-center mb-2 md:mb-0">
                      <div className="w-12 h-12 bg-primary-red text-white rounded-full flex items-center justify-center font-bold mr-4">
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-sm text-primary-red font-semibold">{step.phase}</div>
                        <div className="text-xl font-bold text-charcoal">{step.title}</div>
                      </div>
                    </div>
                    <div className="text-sm text-titanium md:text-right">
                      <span className="bg-white px-3 py-1 rounded-full">{step.duration}</span>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2 ml-16">
                    {step.activities.map((activity, idx) => (
                      <div key={idx} className="flex items-center text-sm text-titanium">
                        <svg className="w-4 h-4 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {activity}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Manufacturing Operations?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Schedule a consultation with our manufacturing technology experts to discuss how our Industry 4.0 solutions can drive efficiency, reduce costs, and improve quality across your operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/services" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-5 bg-white">
        <div className="page-container">
          <h2 className="text-2xl font-bold text-charcoal mb-8 text-center">Related Industry Solutions</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/solutions/automotive" className="card hover:shadow-xl transition-all duration-300 text-center">
              <h3 className="text-lg font-semibold text-charcoal mb-2">Automotive</h3>
              <p className="text-sm text-titanium">Advanced manufacturing for automotive</p>
            </Link>
            <Link to="/solutions/retail-ecommerce" className="card hover:shadow-xl transition-all duration-300 text-center">
              <h3 className="text-lg font-semibold text-charcoal mb-2">Retail & E-commerce</h3>
              <p className="text-sm text-titanium">Supply chain for retail operations</p>
            </Link>
            <Link to="/solutions/energy-utilities" className="card hover:shadow-xl transition-all duration-300 text-center">
              <h3 className="text-lg font-semibold text-charcoal mb-2">Energy & Utilities</h3>
              <p className="text-sm text-titanium">Smart manufacturing systems</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ManufacturingLogistics;